/* eslint-disable */
import {
  SylApi,
  BaseModule,
  Types,
  EventChannel,
} from '@syl-editor/core-utilities';
import { IRenderer } from '@syl-editor/lib/dist/lib/renderer';
import { ToolbarLib } from '@syl-editor/lib/dist/lib/modules/toolbar';
import {debounce} from 'lodash';
import { IToolbarOption } from '@syl-editor/lib/dist/lib/modules/toolbar/toolbar-loader';

// eslint-disable-next-line @typescript-eslint/interface-name-prefix
export interface IToolbarOutlineOption
  extends Omit<IToolbarOption, 'mount' | 'showNames'> {
  threshold?: { top?: number; left?: number; right?: number; bottom?: number }; // 最小边距
  judgeShow?: (editor: SylApi) => boolean;
  zIndex?: number;
}

// eslint-disable-next-line @typescript-eslint/interface-name-prefix
export interface IToolbarOutlineProps {
  editor: SylApi;
  option: IToolbarOutlineOption;
  visible: boolean;
  activeFormat: Types.StringMap<any>;
  toolbarLib: ToolbarLib;
}

const getEditorDOM = (dom: any, topDOM: Element) => {
  if (!dom || dom === topDOM) return;
  if (dom.pmViewDesc) return dom;
  let parentDOM = dom.parentElement;
  while (parentDOM && parentDOM !== topDOM) {
    if (parentDOM.pmViewDesc) return parentDOM;
    parentDOM = parentDOM.parentElement;
  }
};

export class ToolbarOutlineLoader extends BaseModule<IToolbarOutlineOption> {
  private $dom: HTMLElement;

  public bridge: IRenderer<IToolbarOutlineProps>;

  private baseOffset: {
    top: number;
    left: number;
  } = {
    top: 0,
    left: 0,
  };

  private _visible = false;

  private toolbarWidth = 0;

  private shadowKey = 'outline';

  get visible() {
    return this._visible;
  }

  set visible(val) {
    if (!val && this._visible === val) return;
    this._visible = val;

    const newProps: Partial<IToolbarOutlineProps> = { visible: val };
    if (val) {
      this.$dom.style.display = 'block';
      newProps.activeFormat = this.updateFormat(false) as Types.StringMap<any>;
      this.adapter.on(EventChannel.LocalEvent.ON_CHANGE, this.debounceUpdate);
    } else {
      this.$dom.style.display = 'none';
      this.adapter.removeShadow(this.shadowKey);
      this.adapter.off(EventChannel.LocalEvent.ON_CHANGE, this.debounceUpdate);
    }

    this.bridge.setProps(newProps);
  }

  // @ts-ignore
  constructor(adapter: SylApi, originOption: IToolbarOption) {
    const option = { ...originOption };
    super(adapter, option);
    this.option = option;

    this.$dom = document.createElement('div');
    adapter.root.appendChild(this.$dom);
    this.$dom.setAttribute('style','position: absolute;user-select: none;display: none;');

    this.bridge = new option.RenderBridge(
      adapter,
      option.Component,
      this.$dom,
      'toolbarOutline',
    );

    this.visible = false;
    this.bindEvent();
    this.render();

    adapter.addCommand('toolbarOutline', {
      hide: () => this.hide(),
      show: () => this.show(),
      getVisible: () => this.getVisible()
    });

    requestAnimationFrame(() => {
      this.baseOffset = {
        top: (this.adapter.view.dom as HTMLElement).offsetTop,
        left: (this.adapter.view.dom as HTMLElement).offsetLeft,
      };
    });
  }

  private traceDOM = debounce((e, currentTarget?: HTMLElement) => {
    // 获取鼠标移动的目标元素
    let target: any = currentTarget || document.elementFromPoint(e.pageX, e.pageY)
    // 需要排查行内元素
    const inline = ['U','EM','STRONG','S', 'LI', 'FONT', 'SPAN', 'A']
    const displayBlock = ['DIV']
    // 当前元素没有 隐藏toolbar
    if (!target) {
      this.visible = false; 
      (window as any).EditorHoverDom = null;
      return
    };
    // 判断hover的是不是table元素
    let isTableDom = false;
    const table = Array.from(document.getElementsByClassName('tableWrapper'));
    table.forEach((el: any)=>{
      const isFlag = el.contains(target);
      if(isFlag){
        isTableDom = isFlag;
      }
    })
    if(isTableDom){
      return;
    }
    // 是行内元素，则取他的父元素
    target = this.getParentNode(target, inline);
    // 根据当前的target 获取真正的编辑器DOM
    const dom = getEditorDOM(target, this.adapter.view.dom);
    // 如果目标已经存在
    if((window as any).EditorHoverDom === dom)return false
    // 无法获取DOM 或者 如果是DIV元素则不需要tips 则return
    if (!dom || displayBlock.indexOf(dom.nodeName)>-1) {
      this.visible = false; 
      (window as any).EditorHoverDom = null;
      return
    };
    // 全局设置当前的 DOM
    (window as any).EditorHoverDom = dom;
    // 提前移除 Shadow
    this.adapter.removeShadow(this.shadowKey);
    // 获取位置
    let top = 0;
    try{
      const { lineHeight }= window.getComputedStyle(dom, null);
      top = Math.max((parseInt(lineHeight) - 20)/2, 0);
    }catch(e){
      top = 0
    }
    this.$dom.style.top = `${dom.offsetTop + top}px`;
    this.$dom.style.left = `${dom.offsetLeft - 36}px`;
    this.$dom.style.height = `${dom.clientHieght}px`
    // 显示出来
    this.visible = true;
  }, 0);

  private getParentNode = (target: any, inline: any): any => {
    if(inline.indexOf(target.nodeName)>-1 || (target.parentNode && target.parentNode.nodeName === 'BLOCKQUOTE')){
      return this.getParentNode(target.parentNode, inline)
    }
    return target
  }

  private bindEvent = () => {
    this.adapter.view.dom.addEventListener('mousemove', this.traceDOM);
    this.adapter.view.dom.addEventListener('keydown', ()=>{
      const { index } = this.adapter.getSelection();
      const { node } = this.adapter.view.domAtPos(index);
      this.traceDOM(null, node)
    });
  };

  private debounceUpdate = debounce(() => {
    this.updateFormat();
  }, 0);

  private updateFormat(exec = true) {
    const activeFormat = this.adapter.getFormat();
    return exec ? this.bridge.setProps({ activeFormat }) : activeFormat;
  }

  public setProps({ option }: { option: IToolbarOption }) {
    this.option = { ...this.option, ...option };
    this.render();
  }

  public render() {
    this.bridge.setProps({
      editor: this.adapter,
      option: this.option,
      activeFormat: {},
      toolbarLib: new ToolbarLib({ editor: this.adapter, option: this.option }),
    });
  }

  hide = ()=>{
    this.visible = false;
  }

  show = ()=>{
    this.visible = true;
  }
  getVisible = ()=> this.visible;

  public destructor() {
    this.bridge.unmount();
  }
}
